import { useState } from "react";
import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  sharedGuestEndsOnByDefault,
  type AssessmentId,
  type EarlierGuest,
  type EmailAddress,
  type InvolveGuestAsSharedAssessmentReaderRequest,
} from "@thalamos/haven-common";

import type { EarlierGuestsMatch } from "../AskGuestDialog/AskGuestDialog.js";
import type { ShareableAssessment } from "./share_step_parts.js";
import { ShareChooseStep } from "./ShareChooseStep.js";
import { ShareEarlierGuestStep } from "./ShareEarlierGuestStep.js";
import { ShareHeader } from "./ShareHeader.js";
import {
  emptyWhoDraft,
  ShareWhoStep,
  type ShareAttempt,
  type ShareFailure,
  type WhoDraft,
} from "./ShareWhoStep.js";

export type { ShareableAssessment } from "./share_step_parts.js";

/**
 * How sharing went: shared, refused for the address in the server's words,
 * refused because the guest can already read this person's shared
 * assessments or the record has no date of birth, or not reached.
 */
export type ShareWithGuestOutcome =
  | { kind: "shared" }
  | { kind: "address_refused"; message: string }
  | { kind: "already_shared" }
  | { kind: "no_date_of_birth" }
  | { kind: "failed" };

/**
 * Where sharing stands: choosing what to share, saying who and for how long,
 * or saying whether an earlier guest is the one shared with.
 */
type Step =
  | { kind: "choose" }
  | { kind: "who" }
  | {
      kind: "earlier_guest";
      request: InvolveGuestAsSharedAssessmentReaderRequest;
      earlierGuests: readonly EarlierGuest[];
    };

/**
 * Shares finished assessments of one person, read only, with somebody
 * outside the service, in two steps: which assessments, newest first, then
 * who they are and the day their access ends, three months from today unless
 * another is chosen. The warning is ticked afresh every time the dialog
 * opens.
 *
 * Where the address reached a guest before, on a person the sharer reaches,
 * the sharer says whether this is the guest they mean before sharing.
 */
export const ShareWithGuestDialog = ({
  open,
  personName,
  assessments,
  onMatch,
  onShare,
  onClose,
}: {
  open: boolean;
  /** Who the assessments are about, as the dialog names them. */
  personName: string;
  /** The finished assessments the sharer may read, newest first. */
  assessments: readonly ShareableAssessment[];
  onMatch: (email: EmailAddress) => Promisable<EarlierGuestsMatch>;
  onShare: (
    request: InvolveGuestAsSharedAssessmentReaderRequest,
  ) => Promisable<ShareWithGuestOutcome>;
  onClose: () => Promisable<void>;
}) => {
  // A phone gives the whole screen to the dialog rather than a card with margins.
  const onAPhone = useMediaQuery(useTheme().breakpoints.down("sm"));
  const [step, setStep] = useState<Step>({ kind: "choose" });
  const [chosen, setChosen] = useState<ReadonlySet<AssessmentId>>(new Set());
  const [draft, setDraft] = useState<WhoDraft>(emptyWhoDraft);
  const [defaultEndsOn] = useState(() => sharedGuestEndsOnByDefault());
  const [triedToSend, setTriedToSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<ShareWithGuestOutcome>();

  // Newest first, as offered, whatever order they were ticked in.
  const chosenInOrder = assessments.filter(({ assessment }) =>
    chosen.has(assessment.id),
  );
  const failure: ShareFailure | undefined =
    outcome?.kind === "already_shared" ||
    outcome?.kind === "no_date_of_birth" ||
    outcome?.kind === "failed"
      ? outcome.kind
      : undefined;
  const close = () => {
    void onClose();
  };

  const share = async (
    request: InvolveGuestAsSharedAssessmentReaderRequest,
  ) => {
    setOutcome(undefined);
    setSending(true);

    try {
      const shared = await onShare(request);
      setOutcome(shared);
      if (shared.kind === "address_refused") {
        setStep({ kind: "who" });
      }
    } catch {
      setOutcome({ kind: "failed" });
    } finally {
      setSending(false);
    }
  };

  const sendDetails = async (attempt: ShareAttempt) => {
    setTriedToSend(true);

    if (attempt.kind === "incomplete") {
      return;
    }

    setOutcome(undefined);
    setSending(true);

    let matched: EarlierGuestsMatch;
    try {
      matched = await onMatch(attempt.request.email);
    } catch {
      matched = { kind: "failed" };
    }
    setSending(false);

    if (matched.kind === "failed") {
      setOutcome({ kind: "failed" });
      return;
    }

    if (matched.earlierGuests.length > 0) {
      setStep({
        kind: "earlier_guest",
        request: attempt.request,
        earlierGuests: matched.earlierGuests,
      });
      return;
    }

    await share(attempt.request);
  };

  const backToWho = () => {
    setOutcome(undefined);
    setStep({ kind: "who" });
  };

  return (
    <Dialog
      open={open}
      // Nothing closes the dialog while a request is on its way, so its
      // outcome is never lost.
      onClose={sending ? undefined : close}
      maxWidth="sm"
      fullWidth
      fullScreen={onAPhone}
    >
      <ShareHeader personName={personName} at={step.kind} />
      {match(step)
        .with({ kind: "choose" }, () => (
          <ShareChooseStep
            assessments={assessments}
            chosen={chosen}
            onChosenChange={setChosen}
            onCancel={close}
            onNext={() => {
              setStep({ kind: "who" });
            }}
          />
        ))
        .with({ kind: "who" }, () => (
          <ShareWhoStep
            personName={personName}
            chosen={chosenInOrder}
            defaultEndsOn={defaultEndsOn}
            draft={draft}
            onDraftChange={(next) => {
              // A refused address stands until the address is edited.
              if (next.email !== draft.email) {
                setOutcome(undefined);
              }
              setDraft(next);
            }}
            showProblems={triedToSend}
            addressRefusal={
              outcome?.kind === "address_refused" ? outcome.message : undefined
            }
            failure={failure}
            sending={sending}
            onChangeChosen={() => {
              setOutcome(undefined);
              setStep({ kind: "choose" });
            }}
            onCancel={close}
            onShare={(attempt) => {
              void sendDetails(attempt);
            }}
          />
        ))
        .with({ kind: "earlier_guest" }, (asking) => (
          <ShareEarlierGuestStep
            personName={personName}
            email={asking.request.email}
            earlierGuests={asking.earlierGuests}
            failure={failure}
            sending={sending}
            onChangeEmail={backToWho}
            onCancel={close}
            onShare={(earlierGuestUserId) => {
              void share({ ...asking.request, earlierGuestUserId });
            }}
          />
        ))
        .exhaustive()}
    </Dialog>
  );
};
